import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getBannerList, Banner } from "../../api";
import { useCustomColors } from "../../hooks/useCustomColors";

const BannerSlider = () => {
  const colors = useCustomColors();
  const { t } = useTranslation();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    getBannerList()
      .then((response) => setBanners(response.status === 1 ? response.data : []))
      .catch(() => setBanners([]));
  }, []);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const interval = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % banners.length);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [banners.length]);

  if (!banners.length) return null;

  return (
    <Box
      sx={{
        // Explicit width keeps the ratio box full-bleed when maxHeight caps it.
        width: "100%",
        position: "relative",
        overflow: "hidden",
        // Sits between the two banner ratios (2.49 and 2.24) to minimise cropping.
        aspectRatio: "2.36 / 1",
        maxHeight: { xs: "240px", md: "400px" },
        borderRadius: "20px",
        border: `1px solid ${colors.border.accent}`,
        backgroundColor: colors.background.deep,
        boxShadow: colors.card.shadow,
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          transform: `translateX(-${activeIndex * 100}%)`,
          transition: "transform 450ms ease",
        }}
      >
        {banners.map((banner, index) => (
          <Box
            key={banner.code}
            component="img"
            src={banner.image}
            alt={t("banner_alt", { index: index + 1 })}
            sx={{
              display: "block",
              flex: "0 0 100%",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        ))}
      </Box>

      {banners.length > 1 && (
        <Box sx={{ position: "absolute", left: "50%", bottom: "12px", display: "flex", gap: "6px", transform: "translateX(-50%)" }}>
          {banners.map((banner, index) => (
            <Box key={banner.code} sx={{ width: index === activeIndex ? 20 : 6, height: 6, borderRadius: "3px", backgroundColor: index === activeIndex ? colors.accent.gold : "rgba(255,255,255,.55)", transition: "all 250ms ease" }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BannerSlider;
